import { useSuspenseQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { type LoaderFunctionArgs, type RouterContextProvider, useLoaderData, useNavigate } from 'react-router'

import { clientContext } from 'apps/employer_dashboard/router_contexts'
import { pageErrorData } from 'apps/employer_dashboard/utils/errors'
import { BillingPage as BillingPageComponent } from 'components/billing/BillingPage'
import {
  getEmployersEmployerInvoicesYears,
  getEmployersTodos,
  getGetEmployersEmployerInvoicesYearsQueryKey,
  getGetEmployersTodosQueryKey,
} from 'utils/web_api'

const todosQuery = {
  queryKey: getGetEmployersTodosQueryKey({ 'categories[]': ['onboarding'] }),
  queryFn: () => getEmployersTodos({ 'categories[]': ['onboarding'] }),
}

const yearsQuery = {
  queryKey: getGetEmployersEmployerInvoicesYearsQueryKey(),
  queryFn: () => getEmployersEmployerInvoicesYears(),
}

export async function loader ({ context }: LoaderFunctionArgs<RouterContextProvider>) {
  const queryClient = context.get(clientContext)

  try {
    const todos = queryClient?.ensureQueryData(todosQuery)
    const years = queryClient?.ensureQueryData(yearsQuery)

    const [todosData, yearsData] = await Promise.all([todos, years])

    return {
      todosData,
      yearsData,
    }
  } catch (error) {
    throw pageErrorData(error, {
      heading: 'We could not load the billing page',
      message: 'Something unexpected happened. We have been notified and are looking into it.'
    })
  }
}

export function BillingPage () {
  const {
    todosData: initialTodosData,
    yearsData: initialYearsData
  } = useLoaderData<typeof loader>()

  const { data: todosData } = useSuspenseQuery({
    ...todosQuery,
    initialData: initialTodosData,
  })

  const { data: yearsData } = useSuspenseQuery({
    ...yearsQuery,
    initialData: initialYearsData,
  })

  const navigate = useNavigate()

  const goToEmployerInvoice = useCallback((invoiceId: string) => {
    navigate(`employer/${invoiceId}`)
  }, [navigate])

  const goToPlatformFeeInvoice = useCallback((invoiceId: string) => {
    navigate(`platform/${invoiceId}`)
  }, [navigate])

  return (
    <BillingPageComponent
      goToEmployerInvoice={goToEmployerInvoice}
      goToPlatformFeeInvoice={goToPlatformFeeInvoice}
      todosData={todosData}
      yearsData={yearsData}
      isYearsLoading={false}
    />
  )
}
